const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/actions/kisapi/utils.ts';
import Imm from 'immutable';
import { Logger } from '../../services/KisLoggerService';

const Log = Logger('actions/kisapi/utils');

/**
 * Kinds of ClientLog entries. These values are basically arbitrary (though limited in length), but they have to be
 * configured in KISIM Abfragen/Tools to actually see them.
 */
export type ClientLogKind =
    | 'PatDash' // e.g. PatDash's postEndpointStats
    | 'Debug' // generic debug entries (but KISIM uses this as well)
    | 'KisAppAnalytics' // KisApp's loggingSaga
    | 'MobileAnalytics' // PatDash's loggingSaga (can't rename this until Abfragen/Tools is also changed)
    | 'TeamboardAnalytics' // Teamboard's loggingSaga
    | 'Version'; // e.g. postPatDashVersion

/**
 * Key-value pair for compileEndpoint
 */
interface KeyValuePair {
    key: string | null | undefined;
    value: string | null | undefined;
}

/**
 * Helper method to compile a URL endpoint
 * @param pathArr array of path elements; empty elements ('', null, undefined) are filtered out,
 *    the rest is joined by '/'
 * @param queryArr array of key/value pairs that are added as query string; pairs with empty
 *    values (null, undefined) are filtered out
 * @returns ready-to-use URL endpoint
 */
export function compileEndpoint(
    pathArr: (string | null | undefined | '' | false)[],
    queryArr?: (KeyValuePair | null | undefined | '' | false)[] | null
): string {
    queryArr = queryArr || [];

    const path = (pathArr || [])
        .filter(e => !!e)
        .map(pathelem => encodeURIComponent(pathelem as string))
        .join('/');

    const querystring = queryArr
        .filter(qelem => qelem && (qelem as KeyValuePair).value)
        .map(qelem => `${(qelem as KeyValuePair).key}=${encodeURIComponent((qelem as KeyValuePair).value as string)}`)
        .join('&');

    return querystring ? `/${path}?${querystring}` : `/${path}`;
}

/**
 * The immutable map `rec` is converted to a string and then to an immutable map. The resulting
 * immutable map is compared with the original to check if `JSON.stringify` changed any values
 * (typically, undefined values are dropped). If the immutable is changed, a warning is logged.
 * @param rec immutable map to check
 */
export function warnWhenStringifyRemovesAttributes(rec: Imm.Map<string, any>) {
    if (!Imm.is(rec, Imm.fromJS(JSON.parse(JSON.stringify(rec))))) {
        Log.warn('JSON.stringify removed attributes of data which is going to be sent to KISAPI', rec);
    }
}

/**
 * Checks whether KISAPI is alive.
 */
export function apiPingKisApi(forceFail?: boolean) {
    return {
        endpoint: compileEndpoint(['..', 'Status', forceFail ? 'Pong' : 'Ping'], null),
        needsNoAuthentication: true,
    };
}

/**
 * Writes a single entry into ClientLog.
 * @param kind One of the known entry kinds. Must not be empty.
 * @param value Summary or single entry value. May be empty. Will be truncated to 40 chars.
 * @param content arbitrary serializable object
 */
export function apiWriteClientLogEntry(kind: ClientLogKind, value: string, content: unknown) {
    return {
        endpoint: compileEndpoint(['KisMobile', 'ClientLog']),
        requestConfig: {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
                Accept: 'array=no',
            },
            body: JSON.stringify({
                KIND: `${kind}`.trim().slice(0, 40),
                VAL: `${value}`.trim().slice(0, 40),
                CONTENT: content,
            }),
        },
    };
}
